import React from "react";
import "./spinners.css";

/**
 * Drop-in replacements for react-epic-spinners.
 * Avoids broken forwardRef / invalid DOM prop warnings from the unmaintained package.
 * BreedingRhombusSpinner mirrors epic-spinners' 2s 8-child + center animation.
 */

const omitSpinnerOnlyProps = ({
  animationDuration,
  animationDelay,
  delayModifier,
  borderWidth,
  dotsNum,
  ...domSafe
}) => domSafe;

const BaseSpinner = React.forwardRef(function BaseSpinner(
  { color = "#1da193", size = 40, className = "", style, variant = "dots", ...rest },
  ref
) {
  const px = typeof size === "number" ? `${size}px` : size;
  const safe = omitSpinnerOnlyProps(rest);
  return (
    <span
      ref={ref}
      className={`flexhr-spinner flexhr-spinner--${variant} ${className}`.trim()}
      style={{
        width: px,
        height: px,
        color,
        ...style,
      }}
      role="status"
      aria-label="Loading"
      {...safe}
    />
  );
});

export const HollowDotsSpinner = React.forwardRef((props, ref) => (
  <BaseSpinner ref={ref} variant="dots" {...props} />
));

/** Original epic-spinners BreedingRhombus: 8 outward diamonds + pulsing center, 2000ms. */
export const BreedingRhombusSpinner = React.forwardRef(function BreedingRhombusSpinner(
  {
    color = "#1da193",
    size = 65,
    animationDuration = 2000,
    className = "",
    style,
    ...rest
  },
  ref
) {
  const px = typeof size === "number" ? size : parseFloat(size) || 65;
  const durationMs =
    typeof animationDuration === "number" ? animationDuration : 2000;
  const safe = omitSpinnerOnlyProps(rest);

  return (
    <span
      ref={ref}
      className={`breeding-rhombus-spinner ${className}`.trim()}
      style={{
        width: px,
        height: px,
        ["--brs-size"]: `${px}px`,
        ["--brs-color"]: color,
        ["--brs-duration"]: `${durationMs}ms`,
        ...style,
      }}
      role="status"
      aria-label="Loading"
      {...safe}
    >
      {[1, 2, 3, 4, 5, 6, 7, 8].map((n) => (
        <span key={n} className={`rhombus child-${n}`} />
      ))}
      <span className="rhombus big" />
    </span>
  );
});

export const FulfillingSquareSpinner = React.forwardRef((props, ref) => (
  <BaseSpinner ref={ref} variant="square" {...props} />
));

export const CirclesToRhombusesSpinner = React.forwardRef((props, ref) => (
  <BaseSpinner ref={ref} variant="circles" {...props} />
));

export const RadarSpinner = React.forwardRef((props, ref) => (
  <BaseSpinner ref={ref} variant="radar" {...props} />
));

export const PixelSpinner = React.forwardRef((props, ref) => (
  <BaseSpinner ref={ref} variant="pixel" {...props} />
));

export const FlowerSpinner = React.forwardRef((props, ref) => (
  <BaseSpinner ref={ref} variant="flower" {...props} />
));

export default {
  HollowDotsSpinner,
  BreedingRhombusSpinner,
  FulfillingSquareSpinner,
  CirclesToRhombusesSpinner,
  RadarSpinner,
  PixelSpinner,
  FlowerSpinner,
};
